import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, LanguageSwitch, ThemeToggle } from '../layout';

export interface DemoInfo {
  accounts: { key: string; label: string; text: string; company: string; email: string; password: string }[];
  resetsAt: string; suspended: boolean;
  limits: { writes: number; uploads: number; usedWrites: number; usedUploads: number };
}

/** On the demo server only (elsewhere the API answers 404 and nothing is shown). */
export const useDemo = () => useQuery({ queryKey: ['demo'], queryFn: () => api.get<DemoInfo>('api/v1/demo'), retry: false, staleTime: 60_000 });

/** Minutes until the demo is put back. */
export function useMinutesLeft(resetsAt: string | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(timer); }, []);
  return resetsAt ? Math.max(0, Math.ceil((Date.parse(resetsAt) - now) / 60_000)) : null;
}

/** The demo's sign-in: one card per company kind, its e-mail and password shown, and one click to enter. */
export function DemoAccounts({ demo }: { demo: DemoInfo }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const minutes = useMinutesLeft(demo.resetsAt);
  const enter = useMutation({
    mutationFn: (key: string) => api.post('api/v1/demo/login', { key }),
    onSuccess: async () => { queryClient.clear(); await navigate({ to: '/' }); },
  });
  if (demo.suspended) return <Alert kind="error">{t('Demo laikinai išjungta. Bandykite vėliau.')}</Alert>;
  return (
    <section className="demo-accounts">
      <p className="muted">{t('Visos įmonės ir duomenys išgalvoti. Galite drąsiai kurti, keisti ir įkelti — kas valandą viskas atkuriama iš naujo (po {n} min.).', { n: minutes ?? 60 })}</p>
      {enter.isError ? <Alert kind="error">{errorText(t, enter.error)}</Alert> : null}
      <div className="demo-grid">
        {demo.accounts.map((a) => (
          <article key={a.key} className="demo-card">
            <p className="eyebrow">{t(a.label)}</p>
            <h3>{a.company}</h3>
            <p className="muted">{t(a.text)}</p>
            <dl>
              <dt>{t('El. paštas')}</dt><dd><code>{a.email}</code></dd>
              <dt>{t('Slaptažodis')}</dt><dd><code>{a.password}</code></dd>
            </dl>
            <button type="button" className="button button--primary button--small" disabled={enter.isPending} onClick={() => enter.mutate(a.key)}>{t('Prisijungti')}</button>
          </article>
        ))}
      </div>
      <p className="muted account-note">{t('Demo ribos vienam lankytojui iki atkūrimo: {writes} pakeitimų ir {uploads} failų įkėlimų (iki 2 MB). El. laiškai demo režime nesiunčiami.', { writes: demo.limits.writes, uploads: demo.limits.uploads })}</p>
    </section>
  );
}

/** The demo's first page, full width like the panel: the companies to try, and the usual sign-in folded below. */
export function DemoLanding({ demo, login }: { demo: DemoInfo; login: ReactNode }) {
  const { t } = useI18n();
  // Taller than the window: this page scrolls (the panel's own pages scroll inside the panel).
  useEffect(() => { document.body.className = 'login-page demo-body'; window.scrollTo(0, 0); return () => { document.body.className = ''; }; }, []);
  return (
    <main className="demo-page">
      <header className="demo-page__top">
        <span className="login-mark"><img src="media/favicon.png" alt="" width={34} height={34} /><span>FakturaSmart Pro</span><em className="section-soon">{t('Demo')}</em></span>
        <div className="demo-page__tools"><LanguageSwitch /><ThemeToggle /></div>
      </header>
      <section className="demo-page__hero">
        <p className="eyebrow">{t('Demo')}</p>
        <h1>{t('Išbandykite FakturaSmart Pro')}</h1>
        <p className="muted">{t('Pasirinkite įmonę ir dirbkite kaip tikroje programoje: sąskaitos, bankas, mokesčiai, atlyginimai, individuali veikla ir buhalterio panelė.')}</p>
      </section>
      <DemoAccounts demo={demo} />
      <details className="panel-card demo-page__login">
        <summary>{t('Prisijungti el. paštu ir slaptažodžiu')}</summary>
        {login}
      </details>
    </main>
  );
}

/** In place of the AI on the demo: what it does and how its credit works, plainly. */
export function DemoAiNote() {
  const { t } = useI18n();
  return (
    <section className="panel-card demo-ai">
      <p className="eyebrow">{t('AI asistentas')}</p>
      <h2>{t('Kaip veikia AI')}</h2>
      <ul>
        <li>{t('Paprašykite paruošti sąskaitos juodraštį, parodyti skolas, mėnesio suvestinę ar artėjančius terminus — atsakymas iš jūsų įmonės duomenų.')}</li>
        <li>{t('AI perskaito el. paštu gautas sąskaitas ir banko išrašus, kurių neatpažįsta įprastas skaitytuvas.')}</li>
        <li>{t('Premium plane kas mėnesį 1 € kreditas: to užtenka daugeliui klausimų ir dokumentų. Prireikus kreditą galima papildyti 5 €.')}</li>
        <li>{t('Viršuje matote, kiek kredito panaudota. Kai kreditas baigiasi, programa veikia kaip įprasta — tik be AI iki kito mėnesio.')}</li>
      </ul>
      <p className="muted">{t('Demo versijoje AI neįjungtas.')}</p>
    </section>
  );
}
